
import React, { useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AuthContext } from "./AuthProvider";

const PRIVILEGED_ROLES = new Set(["superadmin", "master"]);

const SELF_ROUTES = new Set([
  "/profile",
  "/attendance",
  "/requestemp",
  "/empsettings",
]);

const normalizeRole = (value) =>
  String(value ?? "").trim().toLowerCase();

const normalizeRoute = (value) => {
  const text = String(value ?? "").trim();

  if (!text || text === "*") return text;

  const withSlash = text.startsWith("/")
    ? text
    : `/${text}`;

  return (
    withSlash.replace(/\/+$/, "") || "/"
  ).toLowerCase();
};

const AccessDenied = ({
  title = "Хандах эрх хүрэлцэхгүй байна",
  message,
}) => (
  <main
    role="alert"
    style={{
      minHeight: "70vh",
      display: "grid",
      placeItems: "center",
      padding: 24,
    }}
  >
    <section
      style={{
        maxWidth: 480,
        textAlign: "center",
      }}
    >
      <h2>{title}</h2>
      <p>
        {message ||
          "Администратороос энэ хуудасны эрхийг хүснэ үү."}
      </p>
    </section>
  </main>
);

const PrivateRoute = ({
  children,
  enforcePermissions = false,
  masterOnly = false,
}) => {
  const {
    user,
    loading,
    role,
    permissions,
    isActive,
    error,
    profileMissing,
  } = useContext(AuthContext) || {};

  const location = useLocation();
  const pathname = normalizeRoute(location.pathname);

  if (loading) {
    return (
      <main
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "#f8fafc",
        }}
      >
        <div style={{ fontWeight: 500 }}>
          Эрх шалгаж байна...
        </div>
      </main>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{
          from:
            location.pathname +
            (location.search || "") +
            (location.hash || ""),
        }}
      />
    );
  }

  if (error) {
    return (
      <AccessDenied
        title="Эрхийн мэдээлэл уншигдсангүй"
        message="Firestore Security Rules болон сүлжээний холболтыг шалгана уу."
      />
    );
  }

  if (profileMissing) {
    return (
      <AccessDenied
        title="Эрхийн бүртгэл олдсонгүй"
        message="Хэрэглэгчийн Firestore эрхийн бүртгэлийг шалгана уу."
      />
    );
  }

  if (isActive !== true) {
    return (
      <AccessDenied
        title="Хэрэглэгчийн эрх идэвхгүй байна"
        message="Эрхийн төлөв баталгаажаагүй эсвэл идэвхгүй байна."
      />
    );
  }

  const normalizedRole = normalizeRole(role);

  if (!normalizedRole) {
    return (
      <AccessDenied
        title="Эрх тодорхойгүй байна"
        message="Хэрэглэгчийн role тохируулагдаагүй байна."
      />
    );
  }

  const isPrivileged =
    PRIVILEGED_ROLES.has(normalizedRole);

  // Super Admin болон Master
  if (masterOnly && !isPrivileged) {
    return (
      <AccessDenied
        message="Энэ хуудас зөвхөн Master болон Super Admin эрхтэй хэрэглэгчид нээлттэй."
      />
    );
  }

  // Super Admin болон Master-д ердийн
  // permission шалгалт үйлчлэхгүй.
  if (enforcePermissions && !isPrivileged) {
    const allowed = new Set(
      (Array.isArray(permissions) ? permissions : [])
        .filter((value) => typeof value === "string")
        .map(normalizeRoute)
        .filter(Boolean)
    );

    // Кассын хуучин dashboard эрхийг хадгална.
    const cashierDashboard =
      pathname === "/paymentdashboard" &&
      normalizedRole === "cass" &&
      allowed.has("/dashboard");

    const hasAccess =
      SELF_ROUTES.has(pathname) ||
      allowed.has(pathname) ||
      allowed.has("*") ||
      cashierDashboard;

    if (!hasAccess) {
      return (
        <AccessDenied
          message="Таны permissions жагсаалтад энэ хуудасны эрх байхгүй байна."
        />
      );
    }
  }

  return children;
};

export default PrivateRoute;
